import { useAuthFetch } from "@p0security/frontend-core";
import type {
  DurationOption,
  TenantDurationSettings,
} from "@p0security/shared/configuration/types";
import {
  timeUnitLabelOptions,
  timeUnitLabels,
} from "@p0security/shared/permission-requests/util";
import {
  Col,
  Divider,
  InputNumber,
  Row,
  Select,
  Skeleton,
  Space,
  Typography,
  message,
} from "@p0security/ui";
import { AuthzButton } from "components/common/AuthzButton";
import { useCallback, useEffect, useMemo, useState } from "react";
import { handleErrorWithToastMessage } from "utils/error";

import { useDurationSettings } from "./useDurationSettings";

const style: React.CSSProperties = { padding: "8px 0", flex: "1  1  0px" };

export const DurationSetting: React.FC<{
  description: string;
  field: keyof Omit<TenantDurationSettings, "options">;
  fallback: DurationOption;
  label: string;
  route: string;
}> = ({ description, fallback, field, label, route }) => {
  const [time, setTime] = useState<number | null>(1);
  const [unit, setUnit] = useState<keyof typeof timeUnitLabels>("m");
  const [saving, setSaving] = useState(false);

  const optionsDoc = useDurationSettings();

  // The actual persisted value — used both to sync on load and to revert
  // the inputs if a save is rejected, so the fields never claim to hold
  // something that isn't really saved.
  const savedDuration = useMemo(
    () => optionsDoc.doc?.data[field] || fallback,
    [fallback, field, optionsDoc.doc]
  );

  useEffect(() => {
    setTime(savedDuration.time);
    setUnit(savedDuration.unit);
  }, [savedDuration]);

  const authFetch = useAuthFetch(handleErrorWithToastMessage);

  const setDuration = useCallback(async () => {
    if (time && unit) {
      setSaving(true);
      const response = await authFetch(route, {
        json: { time, unit },
        method: "PUT",
      });
      setSaving(false);
      if (!response) {
        setTime(savedDuration.time);
        setUnit(savedDuration.unit);
        return;
      }
      message.success(`${label} set`);
    }
  }, [time, unit, authFetch, route, label, savedDuration]);

  return (
    <div>
      <Typography.Title level={4}>{label}</Typography.Title>
      <div>
        <Typography.Text type="secondary">{description}</Typography.Text>
        <br />
        {optionsDoc.loading ? (
          <Skeleton.Input active block={true} />
        ) : (
          <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
            <Col className="gutter-row" span={24}>
              <div style={style}>
                <Typography.Text>{label}: </Typography.Text>
                <Space>
                  <Space.Compact>
                    <InputNumber min={1} value={time} onChange={setTime} />
                    <Select
                      options={timeUnitLabelOptions}
                      value={unit}
                      onChange={setUnit}
                      /* eslint-disable-line react/forbid-component-props */ style={{
                        width: 97,
                      }}
                    />
                  </Space.Compact>
                  <Divider />
                  <AuthzButton
                    roles={["owner"]}
                    type="primary"
                    loading={saving}
                    onClick={setDuration}
                  >
                    Set
                  </AuthzButton>
                </Space>
              </div>
            </Col>
          </Row>
        )}
      </div>
    </div>
  );
};
